
import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Scratchgard / Horizon Cart Drawer — refreshed presentation v2.3.
 *
 * Only displays prices returned by Shopify. Does not alter cart/checkout
 * prices and does not edit or replace the original Clay JavaScript.
 *
 * Features:
 * - Re-fetches rendered Shopify drawer section on open/cart updates/login return.
 * - Rechecks an OPEN drawer every 15 seconds (no background polling when closed).
 * - Reconciles genuine line-level discounts with the AJAX cart response.
 * - Hides struck-through comparison price ONLY for an actually discounted line.
 * - Preserves real compare-at prices for normal, non-discounted products.
 * - Keeps original DialogComponent show/close and sticky summary behavior.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @extends {DialogComponent}
 */

const REFRESH_INTERVAL_MS = 15000;
const UPDATE_DEBOUNCE_MS = 300;
const DRAWER_SECTION_FALLBACK = 'cart-drawer-section';

function ensureCartStyles() {
  if (document.getElementById('sg-cart-drawer-price-fix')) return;

  const style = document.createElement('style');
  style.id = 'sg-cart-drawer-price-fix';

  style.textContent = `
    /* Don't flash an obsolete crossed price while a fresh section is loading. */
    cart-drawer-component[data-sgp-cart-syncing="1"]
      .cart-items__unit-price-wrapper :is(s, del, .compare-at-price) {
      visibility: hidden !important;
    }

    /* A real Shopify line discount means we show only the actual final price. */
    cart-drawer-component .cart-items__table-row[data-sgp-cart-discounted="1"]
      .cart-items__unit-price-wrapper :is(s, del, .compare-at-price),
    cart-drawer-component .cart-item[data-sgp-cart-discounted="1"]
      :is(s, del, .compare-at-price),
    cart-drawer-component [data-cart-item][data-sgp-cart-discounted="1"]
      :is(s, del, .compare-at-price) {
      display: none !important;
    }

    /* Original Shopify sale prices remain visible if no cart discount applied. */
    cart-drawer-component .cart-items__table-row[data-sgp-cart-discounted="0"]
      .cart-items__unit-price-wrapper :is(s, del, .compare-at-price) {
      display: inline !important;
      visibility: visible !important;
      opacity: 1 !important;
      text-decoration: line-through !important;
    }
  `;

  document.head.appendChild(style);
}

class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number | null} */
  #syncTimer = null;

  /** @type {number | null} */
  #pollTimer = null;

  /** @type {number | null} */
  #paintTimer = null;

  /** @type {Promise<void> | null} */
  #inFlight = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  #refreshAgain = false;
  #lastCart = null;
  #lastSyncAt = 0;
  #dialogElement = null;

  connectedCallback() {
    super.connectedCallback();

    ensureCartStyles();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#handleOpen
    );

    document.addEventListener(
      'cart:update',
      this.#handleCartMutation
    );

    document.addEventListener(
      'cart:updated',
      this.#handleCartMutation
    );

    document.addEventListener(
      'cart:refresh',
      this.#handleCartMutation
    );

    document.addEventListener(
      'shopify:section:load',
      this.#handleSectionLoad
    );

    window.addEventListener(
      'pageshow',
      this.#handlePageShow
    );

    window.addEventListener(
      'focus',
      this.#handleReturn
    );

    window.addEventListener(
      'storage',
      this.#handleReturn
    );

    document.addEventListener(
      'visibilitychange',
      this.#handleVisibility
    );

    const { dialog } = /** @type {Refs} */ (this.refs);

    this.#dialogElement = dialog || null;

    this.#dialogElement?.addEventListener(
      'close',
      this.#handleClose
    );

    // Observe only this cart drawer for DOM changes.
    this.#observer = new MutationObserver(this.#schedulePaint);

    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      characterData: true
    });

    this.#applyDiscountState();

    if (dialog?.open) {
      this.#handleOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleOpen
    );

    document.removeEventListener(
      'cart:update',
      this.#handleCartMutation
    );

    document.removeEventListener(
      'cart:updated',
      this.#handleCartMutation
    );

    document.removeEventListener(
      'cart:refresh',
      this.#handleCartMutation
    );

    document.removeEventListener(
      'shopify:section:load',
      this.#handleSectionLoad
    );

    window.removeEventListener(
      'pageshow',
      this.#handlePageShow
    );

    window.removeEventListener(
      'focus',
      this.#handleReturn
    );

    window.removeEventListener(
      'storage',
      this.#handleReturn
    );

    document.removeEventListener(
      'visibilitychange',
      this.#handleVisibility
    );

    this.#dialogElement?.removeEventListener(
      'close',
      this.#handleClose
    );

    this.#observer?.disconnect();
    this.#observer = null;

    this.#stopPolling();

    if (this.#syncTimer !== null) {
      clearTimeout(this.#syncTimer);
    }

    if (this.#paintTimer !== null) {
      clearTimeout(this.#paintTimer);
    }
  }

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#afterCartEvent(event);
  };

  #handleCartMutation = (event) => {
    this.#afterCartEvent(event);
  };

  #afterCartEvent(event) {
    const pending = event?.promise;

    if (pending && typeof pending.finally === 'function') {
      pending
        .catch(() => {})
        .finally(() => {
          this.#scheduleSync(UPDATE_DEBOUNCE_MS);
        });
    } else {
      this.#scheduleSync(UPDATE_DEBOUNCE_MS);
    }
  }

  #handleOpen = () => {
    this.#updateStickyState();
    this.#applyDiscountState();

    // Important for guest cart -> login -> reopen.
    this.#scheduleSync(0);
    this.#startPolling();
  };

  #handleClose = () => {
    this.#stopPolling();
  };

  #handlePageShow = () => {
    if (this.#isOpen()) {
      this.#scheduleSync(0);
    }
  };

  #handleReturn = () => {
    if (this.#isOpen()) {
      this.#scheduleSync(120);
    }
  };

  #handleVisibility = () => {
    if (!document.hidden && this.#isOpen()) {
      this.#scheduleSync(120);
    }
  };

  #handleSectionLoad = () => {
    this.#schedulePaint();

    if (this.#isOpen()) {
      this.#scheduleSync(200);
    }
  };

  #isOpen() {
    return Boolean(this.refs.dialog?.open);
  }

  #startPolling() {
    this.#stopPolling();

    this.#pollTimer = setInterval(() => {
      if (
        this.isConnected &&
        this.#isOpen() &&
        !document.hidden &&
        !(
          this.contains(document.activeElement) &&
          document.activeElement?.matches('input, select, textarea')
        )
      ) {
        this.#scheduleSync(0);
      }
    }, REFRESH_INTERVAL_MS);
  }

  #stopPolling() {
    if (this.#pollTimer !== null) {
      clearInterval(this.#pollTimer);
    }

    this.#pollTimer = null;
  }

  #scheduleSync(delay = UPDATE_DEBOUNCE_MS) {
    if (!this.isConnected) return;

    if (this.#syncTimer !== null) {
      clearTimeout(this.#syncTimer);
    }

    this.#syncTimer = setTimeout(() => {
      this.#syncTimer = null;

      void this.#syncCart();
    }, delay);
  }

  #schedulePaint = () => {
    if (this.#paintTimer !== null) return;

    this.#paintTimer = setTimeout(() => {
      this.#paintTimer = null;

      this.#applyDiscountState();
      this.#updateStickyState();
    }, 40);
  };

  #shopRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  #sectionId() {
    const items = this.querySelector(
      'cart-items-component[data-section-id]'
    );

    const wrapper = this.closest(
      '[id^="shopify-section-"]'
    );

    return (
      items?.dataset.sectionId ||
      wrapper?.id.replace(/^shopify-section-/, '') ||
      DRAWER_SECTION_FALLBACK
    );
  }

  async #loadCurrentCart() {
    const response = await fetch(
      `${this.#shopRoot()}cart.js`,
      {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Cart API HTTP ${response.status}`);
    }

    return response.json();
  }

  async #loadRenderedSection() {
    const url = new URL(
      this.#shopRoot(),
      location.origin
    );

    url.searchParams.set(
      'sections',
      this.#sectionId()
    );

    url.searchParams.set(
      '_sg_cart_refresh',
      String(Date.now())
    );

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Section HTTP ${response.status}`
      );
    }

    const sections = await response.json();
    const html = sections[this.#sectionId()];

    if (typeof html !== 'string' || !html.trim()) {
      throw new Error(
        `Shopify cart section '${this.#sectionId()}' missing`
      );
    }

    return html;
  }

  #replaceCartContents(html) {
    const remoteDocument = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const remoteComponent = remoteDocument.querySelector(
      'cart-drawer-component'
    );

    const remoteScope = remoteComponent || remoteDocument;

    // Keep the existing drawer and dialog event listeners.
    // Replace only its server-rendered content.
    const zones = [
      '.cart-drawer__inner',
      'cart-items-component',
      '.cart-drawer__content'
    ];

    for (const selector of zones) {
      const existing = this.querySelector(selector);
      const updated = remoteScope.querySelector(selector);

      if (!existing || !updated) continue;

      existing.replaceWith(
        document.importNode(updated, true)
      );

      return true;
    }

    console.warn(
      '[Scratchgard Cart] Could not locate matching cart region in section response.'
    );

    return false;
  }

  #hasLineDiscount(item) {
    if (!item) return false;

    const original = Number(item.original_price);
    const final = Number(item.final_price);

    // Normal Shopify compare-at sales aren't cart-line discounts.
    return (
      Number.isFinite(original) &&
      Number.isFinite(final) &&
      final < original
    );
  }

  #hasRenderedLineDiscount(row) {
    // Match actual rendered line-level discount allocations.
    return Boolean(
      row.querySelector(
        '.cart-items__variants-wrapper ul[role="list"] li,' +
        '.cart-item__discounts li,' +
        '[data-discount-title]'
      )
    );
  }

  #applyDiscountState() {
    const items = new Map(
      (this.#lastCart?.items || []).map(item => [
        String(item.key),
        item
      ])
    );

    const rows = this.querySelectorAll(
      '.cart-items__table-row[data-key], ' +
      '.cart-item[data-key], ' +
      '[data-cart-item][data-key]'
    );

    rows.forEach(row => {
      const item = items.get(row.dataset.key);

      // Prefer actual Shopify line discounts or rendered allocations.
      const discounted =
        this.#hasRenderedLineDiscount(row) ||
        this.#hasLineDiscount(item);

      const state = discounted ? '1' : '0';

      if (row.dataset.sgpCartDiscounted !== state) {
        row.dataset.sgpCartDiscounted = state;
      }
    });
  }

  async #syncCart() {
    if (this.#inFlight) {
      this.#refreshAgain = true;
      return this.#inFlight;
    }

    const job = async () => {
      this.dataset.sgpCartSyncing = '1';

      try {
        // Read only. Doesn't alter actual cart or checkout prices.
        const [cartResult, sectionResult] =
          await Promise.allSettled([
            this.#loadCurrentCart(),
            this.#loadRenderedSection()
          ]);

        if (!this.isConnected) return;

        if (
          cartResult.status === 'fulfilled' &&
          Array.isArray(cartResult.value?.items)
        ) {
          this.#lastCart = cartResult.value;
        }

        if (sectionResult.status === 'fulfilled') {
          this.#replaceCartContents(sectionResult.value);
        } else {
          console.warn(
            '[Scratchgard Cart] Section refresh failed:',
            sectionResult.reason
          );
        }

        this.#applyDiscountState();
        this.#updateStickyState();

        this.#lastSyncAt = Date.now();
      } catch (error) {
        console.warn(
          '[Scratchgard Cart] Sync failed:',
          error
        );
      } finally {
        delete this.dataset.sgpCartSyncing;
      }
    };

    this.#inFlight = job();

    try {
      await this.#inFlight;
    } finally {
      this.#inFlight = null;

      if (this.#refreshAgain && this.isConnected) {
        this.#refreshAgain = false;
        this.#scheduleSync(200);
      }
    }
  }

  open() {
    this.showDialog();
    this.#handleOpen();

    /**
     * Close drawer when installments CTA is clicked
     * to avoid overlapping dialogs.
     */
    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const installmentsContent = document
          .querySelector('shopify-payment-terms')
          ?.shadowRoot;

        const cta = installmentsContent?.querySelector(
          '#shopify-installments-cta'
        );

        cta?.addEventListener(
          'click',
          this.closeDialog,
          { once: true }
        );
      });
  }

  close() {
    this.#stopPolling();
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);

    if (!dialog) return;

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;

    const ratio = drawerHeight > 0
      ? summaryHeight / drawerHeight
      : 1;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}
